/**
 * Pause-menu renderer: owns the on-screen Pixi overlay (dim + title +
 * scrollable 3-column icon grid + scrollbar + hint). The host (GameScene)
 * builds the items[] array (whose actions know about gameplay state) and
 * calls `show()` whenever the menu must be (re)drawn.
 *
 * Scroll state lives here so that pointer-drag and wheel handlers in the
 * host just forward dy via `scrollBy`.
 */

import { Container, Graphics, Text } from 'pixi.js';
import { drawPauseIcon, type PauseIcon } from './Icons.js';

export type PauseMenuTone = 'primary' | 'danger' | 'muted' | null;

export interface PauseMenuItem {
  icon: PauseIcon;
  label: string;
  tone?: PauseMenuTone;
  action: () => void;
}

export interface PauseMenuHit {
  x: number; y: number; w: number; h: number;
  action: () => void;
}

export interface SafeInsets { top: number; right: number; bottom: number; left: number; }

export interface PauseMenuShowOptions {
  width: number;
  height: number;
  insets: SafeInsets;
  title: string;
  subtitle: string;
  items: PauseMenuItem[];
}

interface ScrollThumb {
  g: Graphics; baseX: number; w: number;
  viewportTop: number; viewportH: number; thumbH: number;
}

/**
 * Owns the scroll state + draw logic for the pause overlay.
 */
export class PauseMenu {
  /** Hit-test rectangles for the current frame's tiles. Public so that
   *  the host's pointer handlers can iterate them directly. */
  readonly hits: PauseMenuHit[] = [];
  scroll = 0;
  scrollMax = 0;
  scrollContent: Container | null = null;
  scrollViewport: { x: number; y: number; w: number; h: number } | null = null;
  private scrollThumb: ScrollThumb | null = null;
  /** Keyboard / gamepad focused item index, or -1 for no focus. */
  focusedIndex = -1;
  private itemPositions: Array<{ x: number; y: number; w: number; h: number }> = [];
  private focusHighlight: Graphics | null = null;

  constructor(private overlay: Container) {}

  /** Tear the overlay down (`visible=false`). Scroll state is preserved
   *  so the next show() can scroll back to the same offset. */
  hide(): void { this.overlay.visible = false; }

  /** Smoothly nudge the scroll offset and update the content + thumb in
   *  place (no rebuild). No-op when there's no scroll headroom. */
  /** Move keyboard/gamepad focus in the given direction within the 3-column grid.
   *  Automatically scrolls the focused tile into view. */
  moveFocus(dir: 'up' | 'down' | 'left' | 'right'): void {
    const count = this.itemPositions.length;
    if (count === 0) return;
    const cols = 3;
    const idx = this.focusedIndex < 0 ? 0 : this.focusedIndex;
    let next: number;
    if (dir === 'right') next = (idx + 1) % count;
    else if (dir === 'left') next = (idx - 1 + count) % count;
    else if (dir === 'down') next = Math.min(idx + cols, count - 1);
    else next = Math.max(idx - cols, 0); // 'up'
    this.focusedIndex = next;
    this.updateFocusHighlight();
    this.scrollFocusedIntoView();
  }

  /** Activate the focused item. Returns true if an action was fired. */
  activateFocused(): boolean {
    if (this.focusedIndex < 0 || this.focusedIndex >= this.hits.length) return false;
    this.hits[this.focusedIndex].action();
    return true;
  }

  /** Reset focus — call when the menu closes. */
  clearFocus(): void {
    this.focusedIndex = -1;
    if (this.focusHighlight) { this.focusHighlight.destroy(); this.focusHighlight = null; }
  }

  private updateFocusHighlight(): void {
    if (this.focusHighlight) { this.focusHighlight.destroy(); this.focusHighlight = null; }
    const idx = this.focusedIndex;
    if (idx < 0 || idx >= this.itemPositions.length || !this.scrollContent) return;
    const pos = this.itemPositions[idx];
    const fg = new Graphics();
    fg.roundRect(pos.x - 3, pos.y - 3, pos.w + 6, pos.h + 6, 17)
      .stroke({ color: 0xffffff, width: 2.5, alpha: 0.9 });
    this.scrollContent.addChild(fg);
    this.focusHighlight = fg;
  }

  private scrollFocusedIntoView(): void {
    const idx = this.focusedIndex;
    if (idx < 0 || idx >= this.itemPositions.length) return;
    const vp = this.scrollViewport;
    if (!vp || this.scrollMax <= 0) return;
    const pos = this.itemPositions[idx];
    const relTop = pos.y - vp.y;
    const relBottom = relTop + pos.h;
    if (relBottom > this.scroll + vp.h) this.scrollBy(relBottom - (this.scroll + vp.h) + 8);
    else if (relTop < this.scroll) this.scrollBy(relTop - this.scroll - 8);
  }

  scrollBy(dy: number): void {
    if (this.scrollMax <= 0 || !this.scrollContent) return;
    this.scroll = Math.max(0, Math.min(this.scrollMax, this.scroll + dy));
    this.scrollContent.y = -this.scroll;
    if (this.scrollThumb) {
      const t = this.scrollThumb;
      const range = t.viewportH - t.thumbH;
      t.g.clear();
      t.g.roundRect(t.baseX, t.viewportTop + (this.scroll / this.scrollMax) * range, t.w, t.thumbH, t.w / 2)
        .fill({ color: 0xffffff, alpha: 0.55 });
    }
  }

  /** Rebuild the entire overlay. Call on initial show or whenever any
   *  toggle (mute, fullscreen, level change) might have changed labels. */
  show(opts: PauseMenuShowOptions): void {
    const { overlay } = this;
    overlay.removeChildren();
    this.hits.length = 0;
    this.focusHighlight = null; // destroyed with the old content container
    const { width: w, height: h, insets, items } = opts;

    const dim = new Graphics();
    dim.rect(0, 0, w, h).fill({ color: 0x000000, alpha: 0.55 });
    overlay.addChild(dim);

    const titleY = 28 + insets.top;
    const title = new Text({
      text: opts.title,
      style: { fill: 0xffffff, fontSize: 32, fontFamily: 'system-ui', fontWeight: '800' },
    });
    title.anchor.set(0.5);
    title.x = w / 2; title.y = titleY;
    overlay.addChild(title);

    const subtitle = new Text({
      text: opts.subtitle,
      style: { fill: 0xbbb0e0, fontSize: 14, fontFamily: 'system-ui', fontWeight: '500' },
    });
    subtitle.anchor.set(0.5);
    subtitle.x = w / 2; subtitle.y = titleY + 22;
    overlay.addChild(subtitle);

    // 3-column icon grid; rows wrap, scrolls vertically if it overflows.
    const cols = 3;
    const tileW = 110, tileH = 110, gap = 12;
    const gridW = cols * tileW + (cols - 1) * gap;
    const rows = Math.ceil(items.length / cols);
    const totalContentH = rows * tileH + (rows - 1) * gap;
    const vpTop = titleY + 56;
    const vpBottom = h - insets.bottom - 36;
    const vpH = Math.max(120, vpBottom - vpTop);
    const vpX = (w - gridW) / 2 - 8;
    const vpW = gridW + 16;

    const viewport = new Container();
    overlay.addChild(viewport);
    const mask = new Graphics();
    mask.rect(vpX, vpTop, vpW, vpH).fill(0xffffff);
    overlay.addChild(mask);
    viewport.mask = mask;
    const content = new Container();
    viewport.addChild(content);

    const gridX = (w - gridW) / 2;
    items.forEach((it, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gridX + col * (tileW + gap);
      const y = vpTop + row * (tileH + gap);
      drawPauseTile(content, x, y, tileW, tileH, it.icon, it.label, it.tone ?? null);
      this.hits.push({ x, y, w: tileW, h: tileH, action: it.action });
    });

    this.itemPositions = this.hits.map(h => ({ x: h.x, y: h.y, w: h.w, h: h.h }));
    if (this.focusedIndex >= 0 && this.focusedIndex < items.length) {
      const pos = this.itemPositions[this.focusedIndex];
      const fg = new Graphics();
      fg.roundRect(pos.x - 3, pos.y - 3, pos.w + 6, pos.h + 6, 17)
        .stroke({ color: 0xffffff, width: 2.5, alpha: 0.9 });
      content.addChild(fg);
      this.focusHighlight = fg;
    }

    const maxScroll = Math.max(0, totalContentH - vpH);
    this.scrollMax = maxScroll;
    this.scroll = Math.min(this.scroll, maxScroll);
    content.y = -this.scroll;
    this.scrollContent = content;
    this.scrollViewport = { x: vpX, y: vpTop, w: vpW, h: vpH };

    if (maxScroll > 0) {
      const sbX = vpX + vpW - 4;
      const sbW = 3;
      const trackBg = new Graphics();
      trackBg.roundRect(sbX, vpTop, sbW, vpH, sbW / 2)
        .fill({ color: 0xffffff, alpha: 0.12 });
      overlay.addChild(trackBg);
      const thumbH = Math.max(24, vpH * (vpH / totalContentH));
      const thumbY = vpTop + (this.scroll / maxScroll) * (vpH - thumbH);
      const thumb = new Graphics();
      thumb.roundRect(sbX, thumbY, sbW, thumbH, sbW / 2)
        .fill({ color: 0xffffff, alpha: 0.55 });
      overlay.addChild(thumb);
      this.scrollThumb = { g: thumb, baseX: sbX, w: sbW, viewportTop: vpTop, viewportH: vpH, thumbH };
    } else {
      this.scrollThumb = null;
    }

    const hint = new Text({
      text: maxScroll > 0
        ? 'Scroll for more  \u2014  \u2191\u2193\u2190\u2192\u00b7Enter navigate  \u2014  P/Esc resume'
        : '\u2191\u2193\u2190\u2192 navigate  \u00b7  Enter select  \u00b7  P/Esc resume',
      style: { fill: 0xffffff, fontSize: 13, fontFamily: 'system-ui', fontStyle: 'italic' },
    });
    hint.anchor.set(0.5);
    hint.x = w / 2; hint.y = vpBottom + 16;
    overlay.addChild(hint);

    overlay.visible = true;
  }
}

/** Render a single pause-menu tile (rounded card + vector icon + label). */
function drawPauseTile(
  parent: Container,
  x: number, y: number, w: number, h: number,
  icon: PauseIcon, label: string,
  tone: PauseMenuTone,
): void {
  const bgFill = tone === 'primary' ? 0x3a2a6e : tone === 'danger' ? 0x4a1f2a : 0x1a1a2e;
  const stroke = tone === 'primary' ? 0xaa88ff : tone === 'muted' ? 0x555566 : 0x6655aa;
  const iconColor = tone === 'muted' ? 0x888899 : 0xffffff;
  const labelColor = tone === 'muted' ? 0xaaaabb : 0xffffff;

  const card = new Graphics();
  card.roundRect(x, y, w, h, 14)
    .fill({ color: bgFill, alpha: 0.92 })
    .stroke({ color: stroke, width: 1.5, alpha: 0.85 });
  parent.addChild(card);

  const iconG = new Graphics();
  drawPauseIcon(iconG, icon, x + w / 2, y + 38, 22, iconColor);
  parent.addChild(iconG);

  const t = new Text({
    text: label,
    style: { fill: labelColor, fontSize: 13, fontFamily: 'system-ui', fontWeight: '600' },
  });
  t.anchor.set(0.5);
  t.x = x + w / 2; t.y = y + h - 18;
  parent.addChild(t);
}
